import type { UserSummary } from "@dealer-center/ddc-pages-configuration-interface";
import type { UserInfoSubject } from "../components/UserInfo";

/** Why no subject could be resolved. Distinguished because the UI says something different for each. */
export const SUBJECT_UNAVAILABLE_REASONS = [
  /** Nobody is selected yet — the ordinary first-render state, not a failure. */
  "no-selection",
  /**
   * The ControlCenter outlet context is absent, so there is no account to resolve against. A wiring
   * error — but see the hook's comment on why it is reported rather than thrown.
   */
  "no-context"
] as const;

export type SubjectUnavailableReason =
  (typeof SUBJECT_UNAVAILABLE_REASONS)[number];

export interface UseUserManagerSubjectResult {
  /** The resolved subject, or `undefined` when `unavailableReason` is set. Never partial. */
  subject?: UserInfoSubject;
  unavailableReason?: SubjectUnavailableReason;
}

/**
 * `firstName` + `lastName`, trimmed, falling back to `userId`.
 *
 * The same rule `resolveDisplayName` applies in `useMyInfoSubject`, and for the same reason: CC9
 * returns `""` (not `null`) for unset strings, so a user with **neither** name is a real case rather
 * than a failure. Never render a blank row or a blank header.
 */
const resolveDisplayName = (user: UserSummary): string => {
  const name = [user.firstName, user.lastName]
    .filter(part => Boolean(part?.trim()))
    .join(" ")
    .trim();

  return name || user.userId;
};

/**
 * Resolves the subject of the User Manager detail panel from the **selected row** plus the account
 * in context.
 *
 * **Deliberately area-private, and deliberately does NOT throw** — the two ways it differs from
 * `useMyInfoSubject`, which shares its return *type* but not its behavior (research R-002).
 *
 * `useMyInfoSubject` throws when the outlet context is absent, which is right there: My Info's
 * subject is implied, so a missing context means the page was mounted outside ControlCenter and
 * should fail loudly rather than render a blank form. Here a throw would be **wrong**: FR-008
 * requires that a user who cannot be loaded produce a failure **for that user** while the list
 * stays usable so another can be selected, and the Edge Cases spell out the two things that must
 * not happen — "not a blank panel and not the previous user's data left on screen". A throw takes
 * the whole page, list included, to the app-level `ErrorBoundary`. So this hook **reports** and the
 * shell renders the failure beside a working list.
 *
 * **The account is passed in, not read from context here.** `MyInfo.tsx`'s invariant — "No hook
 * below this point re-reads the outlet context … two readers could drift, and in an 'as' context
 * drifting means showing one user's header over another's data" — is why. User Manager needs
 * **two** things from that context (the operator's id, for their authority self-reads, and the
 * account, for every subject read), so the shell reads it **once** and hands both down. A second
 * reader in here would be the drift the invariant names.
 *
 * Three resolution rules, each load-bearing:
 *
 * 1. **`accountId` is the account in context, never anything off the row.** In an "as" context that
 *    is the as-account, which is what FR-029 requires: every read must resolve against the account
 *    in context, and no request may fall back to the operator's own account.
 * 2. **`displayName` is derived from the row**, not from the context. The context's name is copied
 *    off the session JWT and describes the **operator**, so using it would label every row with the
 *    signed-in user's name. (`IdentityHeader` takes the header's name from
 *    `useGetUserInfo().data.summary` regardless — "Do not reintroduce `subject.displayName` as the
 *    header's name." This field feeds the list row and the unsaved-changes prompt.)
 * 3. **`isBridgeUser` is `false` for every subject** — see the assignment below.
 */
export const useUserManagerSubject = (
  selectedUser?: UserSummary,
  accountId?: string
): UseUserManagerSubjectResult => {
  if (!accountId) {
    return { unavailableReason: "no-context" };
  }
  if (!selectedUser) {
    return { unavailableReason: "no-selection" };
  }

  return {
    subject: {
      userId: selectedUser.userId,
      accountId,
      displayName: resolveDisplayName(selectedUser),
      username: selectedUser.userId,
      /*
       * ⚠️ HARDCODED `false` — still, but **no longer for want of a source** (revised 2026-09-17).
       *
       * R-003 recorded that no per-user bridge source existed. It was wrong: CC9
       * `/user-profile/{ccUsername}` returns `username`, the **Bridge** username, per user, and
       * `useGetBridgeId` now reads it for the identity header. So the id is available here for the
       * asking.
       *
       * What is **not** settled is deriving bridge *status* from it. A present Bridge ID is strong
       * evidence and not a ratified rule, and this flag gates a password action (FR-056) — so it
       * stays `false` until Story 6 decides, rather than being inferred in passing by the change
       * that added a display field. Wiring it is a Story 6 task; see R-003's resolution note.
       *
       * The old session-scoped signal remains the wrong answer and must not be substituted:
       * `bridgePlatformId` / `bridge_user_name` on ControlCenter's outlet context is looked up
       * against `internalUserId || userId`, so it describes the **authenticated session** and
       * reports the operator's identity provider even while impersonating — not merely unavailable
       * per-user, actively wrong per-user. `UserDetail` carries no bridge field, and `platformId` is
       * **not** the Bridge ID either: it is a GUID, observed null live (`user-search` has it
       * commented out as "Platform ID" beside the real one).
       *
       * **Consequence, recorded rather than hidden: FR-056 cannot be honoured for anyone.** With
       * this `false`, the page has no way to identify a bridge user. That is a **Story 6 blocker**,
       * not a read-side one: FR-083c opens the Reset Password dialog soft-disabled
       * (`isWriteAvailable={false}`), so it can act for no subject and says so to every subject,
       * leaving no bridge-specific state to distinguish. It hard-blocks the mutation — the moment
       * the fields go live, the page offers resets to bridge users it cannot reset.
       *
       * **Do NOT paper over this** by guessing bridge status from `platformId` or from a `userId`
       * pattern. A wrong guess withholds Reset Password from a user who can use it, or offers it to
       * one who cannot.
       */
      isBridgeUser: false
    }
  };
};
